// Tareas "Por hacer" de la ficha del presupuesto (formato nuevo, diseño A+).
//
// Todo se calcula con datos que ya existen en el presupuesto: no hay
// colecciones ni campos nuevos. La función principal es pura (recibe los datos
// ya preparados y la fecha de hoy) para poder añadir con facilidad las tareas
// de fases posteriores y probarla sin montar la página.
//
// Estados de una tarea:
//   'hecha'     · ya está resuelta
//   'pendiente' · hay algo que hacer (cuenta en el contador de la pestaña)
//   'bloqueada' · todavía no se puede hacer (p. ej. facturar antes del evento)
//   'info'      · informativa: no cuenta como hecha ni como pendiente
//
// La acción de cada tarea es una clave ('enviar_presupuesto', 'confirmar',
// 'ir_lineas', 'ir_facturacion', 'orden_cocina', 'emitir_factura', 'rapido');
// la ficha la traduce al handler existente. 'rapido' abre un modal con el
// bloque de la ficha que resuelve la tarea (campo `modal`: 'direccion',
// 'furgonetas', 'personal', 'bebidas', 'contacto_facturacion', 'alergenos',
// 'solicitud_extras' o 'alquiler', que abre el panel de alquiler de la pestaña
// Material) sin salir de la ficha; al guardar, la tarea se
// recalcula sola con los datos nuevos del presupuesto.
//
// Multievento (d.jornadas, ver `jornadasParaTareas`): dirección, hoja de
// material, orden de cocina y, si hay quién va por jornada, personal salen
// como subtareas de cada jornada (campo `jornada`: { id, numero, etiqueta });
// en facturación por jornada, también la factura. La ficha las pinta
// agrupadas bajo "Jornada N · dd/mm". Un evento de un día no cambia.

import { parsearFechaServidor } from './pantallasUnDia.js';

export const DIAS_LIMITE_CAMBIOS = 7;
export const HORAS_ORDEN_COCINA = 48;
// Pago del alquiler de material al proveedor: 7 días antes del evento
export const DIAS_PAGO_ALQUILER = 7;
// Cobro previo: "Pedir pago" se ofrece cuando faltan estos días o menos para el evento
export const DIAS_PEDIR_COBRO_PREVIO = 10;
// "Hacer la compra" (lista de compra) pasa a pendiente estos días antes del evento
export const DIAS_HACER_COMPRA = 7;

/**
 * Tarea "Hacer la compra" de un confirmado futuro, con el `compra_resumen` del
 * GET del presupuesto ({ con_seguimiento, pendientes, pedidas, compradas,
 * actualizado_en } o null si la lista aún no tiene seguimiento). null si no aplica.
 */
export const tareaCompra = (compraResumen, { hoy, fechaInicio, confirmado, celebrado, cerrado }) => {
  if (!confirmado || celebrado || cerrado || compraResumen === undefined || !fechaInicio) return null;
  const cr = compraResumen;
  const accion = { tipo: 'ir_compra', abrirPestana: 'compra', etiqueta: 'Abrir lista' };
  const quedan = !cr || (cr.con_seguimiento || 0) === 0 || (cr.pendientes || 0) > 0;
  if (!quedan) {
    const partes = [];
    if (cr.pedidas > 0) partes.push(plural(cr.pedidas, 'línea pedida', 'líneas pedidas'));
    if (cr.compradas > 0) partes.push(plural(cr.compradas, 'comprada', 'compradas'));
    return { clave: 'compra', titulo: 'Compra hecha', estado: 'hecha', descripcion: partes.join(' · ') || 'Nada pendiente en la lista de compra', accion: null };
  }
  const desde = sumarDias(fechaInicio, -DIAS_HACER_COMPRA);
  if (hoy < desde) {
    return {
      clave: 'compra',
      titulo: 'Hacer la compra',
      estado: 'bloqueada',
      descripcion: `Desde el ${formatoDiaMes(desde)} (${DIAS_HACER_COMPRA} días antes del evento)`,
      accion,
    };
  }
  return {
    clave: 'compra',
    titulo: 'Hacer la compra',
    estado: 'pendiente',
    descripcion: cr && cr.con_seguimiento > 0
      ? `${cr.pendientes} ${cr.pendientes === 1 ? 'línea pendiente' : 'líneas pendientes'} de ${cr.con_seguimiento}`
      : 'Revisa la lista de compra y pide a cada proveedor',
    accion: { ...accion, principal: true },
  };
};

/**
 * Tarea "Revisar pedidos ya hechos para estos días" (aviso de pedidos cortos,
 * 05-oct-2026): otros eventos de los mismos días ya tienen líneas pedidas a
 * estos proveedores (`compra_resumen.pedidos_en_sus_dias`), así que este evento
 * puede dejar cortos esos pedidos. Solo en un confirmado futuro que aún no ha
 * pedido nada; abre la pestaña Lista de compra. null si no aplica.
 */
export const tareaPedidosEnSusDias = (compraResumen, { confirmado, celebrado, cerrado }) => {
  if (!confirmado || celebrado || cerrado || !compraResumen) return null;
  const pedidos = Array.isArray(compraResumen.pedidos_en_sus_dias) ? compraResumen.pedidos_en_sus_dias : [];
  if (pedidos.length === 0) return null;
  if ((compraResumen.pedidas || 0) > 0 || (compraResumen.compradas || 0) > 0) return null;
  const nombres = pedidos.map(p => p.proveedor_nombre).filter(Boolean);
  const numeros = Array.from(new Set(pedidos.flatMap(p => p.numeros || []))).sort((a, b) => String(a).localeCompare(String(b), 'es', { numeric: true }));
  return {
    clave: 'pedidos_dias',
    titulo: `Revisar pedidos ya hechos para estos días (${nombres.join(', ')})`,
    estado: 'pendiente',
    descripcion: `Ya se ha pedido para ${numeros.length === 1 ? 'el evento' : 'los eventos'} ${numeros.join(', ')}: pide solo lo que falta (Compras y Almacén > Compra de la semana)`,
    accion: { tipo: 'ir_compra', abrirPestana: 'compra', etiqueta: 'Abrir lista' },
  };
};

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// 'YYYY-MM-DD...' -> Date a las 00:00 locales (null si no es válida)
export const parsearFechaDia = (valor) => {
  if (!valor) return null;
  const texto = String(valor).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) return null;
  const fecha = new Date(`${texto}T00:00:00`);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

export const inicioDelDia = (fecha) => {
  const f = new Date(fecha);
  f.setHours(0, 0, 0, 0);
  return f;
};

export const sumarDias = (fecha, dias) => {
  const f = new Date(fecha);
  f.setDate(f.getDate() + dias);
  return f;
};

export const diasEntre = (desde, hasta) => Math.round((inicioDelDia(hasta) - inicioDelDia(desde)) / 86400000);

// dd/mm
export const formatoDiaMes = (fecha) => (
  fecha ? `${String(fecha.getDate()).padStart(2, '0')}/${String(fecha.getMonth() + 1).padStart(2, '0')}` : ''
);

// "8 oct"
// Fecha y hora guardadas en UTC sin zona (datetime.utcnow().isoformat()) a "dd/mm HH:MM" local
export const formatoFechaHoraUtc = (valor) => {
  if (!valor) return '';
  const texto = String(valor);
  const f = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(texto) ? texto : `${texto}Z`);
  if (Number.isNaN(f.getTime())) return '';
  return `${formatoDiaMes(f)} ${String(f.getHours()).padStart(2, '0')}:${String(f.getMinutes()).padStart(2, '0')}`;
};

export const formatoDiaMesCorto = (fecha) => (fecha ? `${fecha.getDate()} ${MESES_CORTOS[fecha.getMonth()]}` : '');

// Primer y último día del evento (en multievento, la primera y la última jornada)
export const obtenerFechasEvento = (presupuesto) => {
  if (!presupuesto) return { inicio: null, fin: null };
  const jornadas = presupuesto.es_multi_evento && Array.isArray(presupuesto.jornadas) ? presupuesto.jornadas : [];
  const fechasJornadas = jornadas.map(j => parsearFechaDia(j?.fecha)).filter(Boolean).sort((a, b) => a - b);
  const principal = parsearFechaDia(presupuesto.fecha_evento);
  const inicio = fechasJornadas[0] || principal;
  const fin = parsearFechaDia(presupuesto.es_multi_evento && presupuesto.fecha_ultima_jornada)
    || fechasJornadas[fechasJornadas.length - 1]
    || principal;
  return { inicio, fin };
};

// Fecha límite de cambios: 7 días antes del (primer día del) evento
export const calcularFechaLimiteCambios = (fechaInicioEvento) => (
  fechaInicioEvento ? sumarDias(fechaInicioEvento, -DIAS_LIMITE_CAMBIOS) : null
);

// Orden de cocina (nueva): convive con la actual, que es la que se envía
export const TEXTO_ORDEN_NUEVA_PRUEBAS = 'Versión en pruebas: la orden que se envía sigue siendo la actual';

// Orden de cocina: enviar 48 h antes del evento
export const calcularFechaOrdenCocina = (fechaInicioEvento) => (
  fechaInicioEvento ? sumarDias(fechaInicioEvento, -Math.round(HORAS_ORDEN_COCINA / 24)) : null
);

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// 10125.5 -> '10.125,50 €' (sin depender de la agrupación del navegador)
export const formatoEur = (valor) => {
  const n = Number(valor) || 0;
  const [entera, decimales] = Math.abs(n).toFixed(2).split('.');
  return `${n < 0 ? '-' : ''}${entera.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimales} €`;
};

// Tarea "Reenviar el presupuesto al cliente" (cambios después del último envío)
const tareaReenviar = (envio) => {
  const cambios = Array.isArray(envio.cambios) ? envio.cambios : [];
  // U-95: fecha guardada en UTC sin zona
  const fechaEnvio = envio.fecha ? parsearFechaServidor(envio.fecha) : null;
  const cuando = fechaEnvio ? ` del ${formatoDiaMes(fechaEnvio)}` : '';
  const primeros = cambios.slice(0, 3).join('; ');
  const resto = cambios.length > 3 ? `; y ${cambios.length - 3} más` : '';
  return {
    clave: 'reenviar',
    titulo: 'Reenviar el presupuesto al cliente',
    estado: 'pendiente',
    descripcion: `Ha cambiado después del envío${cuando}${primeros ? `: ${primeros}${resto}` : ''}`,
    accion: { tipo: 'enviar_presupuesto', etiqueta: 'Reenviar', principal: true },
  };
};

// Palabras de alergia o dieta en las observaciones (sin acentos ni mayúsculas)
// Ruta desde la sede (U-02, 06-oct-2026): ya no se calcula sola al abrir la
// ficha. Se calcula en el alta, al guardar con otra dirección o con el botón.
export const TEXTO_SIN_RUTA = 'Sin ruta calculada: pulsa Calcular ruta';
export const TEXTO_RUTA_NO_CALCULADA = 'No se ha podido calcular la ruta: revisa la dirección y pulsa Calcular ruta';

// Misma normalización que `_normalizar` del backend (reglas comerciales)
const normalizarDireccionRuta = (texto) => String(texto || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/-/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

// Aviso de la ruta del presupuesto, o null si está al día o no hay dirección:
// Google no encontró esta dirección, falta km o minutos, o la ruta es de otra
// dirección (mismo criterio que `estado_ruta` del backend).
export const avisoRutaPresupuesto = (p) => {
  if (!p) return null;
  const clave = normalizarDireccionRuta(p.direccion_exacta || p.lugar_evento);
  if (!clave) return null;
  if (normalizarDireccionRuta(p.ruta_error_origen) === clave) return TEXTO_RUTA_NO_CALCULADA;
  const sinRuta = !(parseFloat(p.distancia_km) > 0) || !(parseFloat(p.tiempo_viaje_min) > 0);
  const desfasada = !sinRuta && !!p.ruta_origen && normalizarDireccionRuta(p.ruta_origen) !== clave;
  return sinRuta || desfasada ? TEXTO_SIN_RUTA : null;
};

// Hora de fin sin definir: con hora de inicio y sin hora de fin, o la misma
// (se deja así cuando no se sabe hasta qué hora es). El horario y el coste se
// calculan con una hora de fin provisional (2 h con un servicio, 4 h con
// varios); tarea para confirmarla.
// Mismo criterio que `fin_sin_definir` del backend.
const minutosHora = (v) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v || '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
export const finSinDefinir = (horaInicio, horaFin) => {
  const ini = minutosHora(horaInicio);
  if (ini === null) return false;
  const fin = minutosHora(horaFin);
  return fin === null || fin === ini;
};
export const TEXTO_FIN_SIN_DEFINIR = 'El horario y el coste se calculan con una hora de fin provisional (2 h con un servicio, 4 h con varios). Confírmala poniendo la hora de fin real.';
export const avisoFinSinDefinir = (p) => {
  if (!p) return null;
  const jornadas = Array.isArray(p.jornadas) ? p.jornadas.filter(j => j && typeof j === 'object') : [];
  if (p.es_multi_evento && jornadas.length > 0) {
    const sin = jornadas.filter(j => finSinDefinir(j.hora_inicio || p.hora_inicio, j.hora_fin || (j.hora_inicio ? '' : p.hora_fin)));
    if (sin.length === 0) return null;
    return `${sin.length === 1 ? 'Una jornada' : `${sin.length} jornadas`} sin hora de fin. ${TEXTO_FIN_SIN_DEFINIR}`;
  }
  return finSinDefinir(p.hora_inicio, p.hora_fin) ? `Sin hora de fin (${p.hora_inicio}). ${TEXTO_FIN_SIN_DEFINIR}` : null;
};

export const PALABRAS_ALERGIA_DIETA = [
  'sin gluten', 'celiac', 'alergi', 'alergic', 'intoleran', 'vegan', 'vegetarian',
  'halal', 'kosher', 'lactosa', 'frutos secos', 'marisco', 'huevo',
];

const normalizarTexto = (texto) => String(texto || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

// Restricciones fuera de los 14 alérgenos oficiales (mismo criterio que
// `restricciones_no_oficiales` del backend): un ingrediente cuenta si va con
// una negación ("no come ajo", "sin cebolla"); las dietas cuentan siempre.
// Se tienen en cuenta en cocina, pero no se garantizan.
export const TERMINOS_NO_OFICIALES = [
  'ajo', 'ajos', 'cebolla', 'cebollas', 'puerro', 'picante', 'picantes', 'guindilla', 'pimienta',
  'pimiento', 'pimientos', 'tomate', 'cilantro', 'perejil', 'champinon', 'champinones', 'setas',
  'fresa', 'fresas', 'kiwi', 'melocoton', 'platano', 'pina', 'maiz', 'legumbres', 'garbanzos',
  'cerdo', 'carne', 'carnes', 'ternera', 'pollo', 'cordero', 'azucar', 'sal', 'alcohol', 'fructosa',
];
const DIETAS_SIEMPRE = ['vegetarian', 'vegan', 'halal', 'kosher', 'embarazad', 'diabetic', 'fodmap', 'histamina', 'musulman'];
const RE_NEGACION = /(^|[^a-z])(no|sin|nada de|alergi[a-z]*|alergic[a-z]*|intoleran[a-z]*|evita[a-z]*|excepto|salvo)(?![a-z])/;
const contienePalabra = (t, palabra) => new RegExp(`(^|[^a-z])${palabra}(?![a-z])`).test(t);

// ¿El texto menciona una restricción no oficial? ("1 persona no come ajo", "2 vegetarianos")
export const mencionaRestriccionNoOficial = (texto) => {
  const frases = normalizarTexto(texto).split(/[\n\r;|.!?]+/);
  return frases.some((f) => {
    if (DIETAS_SIEMPRE.some(d => f.includes(d))) return true;
    return RE_NEGACION.test(f) && TERMINOS_NO_OFICIALES.some(p => contienePalabra(f, p));
  });
};

// ¿El texto menciona una alergia o una dieta? ("1 MENU SIN GLUTEN", "celíaco",
// "no come ajo"...)
export const mencionaAlergiaODieta = (texto) => {
  const t = normalizarTexto(texto);
  return !!t && (PALABRAS_ALERGIA_DIETA.some(p => t.includes(p)) || mencionaRestriccionNoOficial(t));
};

// Observaciones generales del presupuesto y de cada jornada en un solo texto
export const textoObservacionesPresupuesto = (p) => {
  if (!p) return '';
  const partes = [p.observaciones];
  (Array.isArray(p.jornadas) ? p.jornadas : []).forEach(j => { if (j) partes.push(j.observaciones); });
  return partes.filter(x => typeof x === 'string' && x.trim()).join(' | ');
};

export const TEXTO_ALERGIA_SIN_REGISTRAR = 'Las observaciones mencionan una alergia o dieta sin registrar';

// Multievento: "Total 1.842,50 €: 1.600,50 € de las jornadas y 242,00 € de
// suplementos", con el alquiler de evento si lo hay ("..., 87,50 € de
// suplementos y 13,61 € de alquiler") y las bebidas del evento (D9). Vacío si
// no hay suplementos del evento, alquiler ni bebidas, o si no es multievento.
export const textoDetalleImporteCobroPrevio = (cp) => {
  if (!cp || !cp.es_multievento) return '';
  const partes = [];
  if (Number(cp.importe_suplementos) > 0) partes.push(`${formatoEur(cp.importe_suplementos)} de suplementos`);
  if (Number(cp.importe_alquiler) > 0) partes.push(`${formatoEur(cp.importe_alquiler)} de alquiler`);
  if (Number(cp.importe_bebidas) > 0) partes.push(`${formatoEur(cp.importe_bebidas)} de bebidas`);
  if (partes.length === 0) return '';
  const resto = partes.length === 1
    ? ` y ${partes[0]}`
    : `, ${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
  return `Total ${formatoEur(cp.importe_total)}: ${formatoEur(cp.importe_jornadas)} de las jornadas${resto}`;
};

// Tarea "Cobro previo" (100 % siete días antes del evento), con el estado del
// GET /presupuestos/{id}/cobro-previo. null si no toca mostrarla.
export const tareaCobroPrevio = (cp, hoyEntrada) => {
  if (!cp || !['cobrado', 'aplazado', 'pendiente'].includes(cp.motivo)) return null;
  if (cp.motivo === 'cobrado') {
    return { clave: 'cobro-previo', titulo: 'Cobro previo', estado: 'hecha', descripcion: 'Cobrado por adelantado', accion: null };
  }
  if (cp.motivo === 'aplazado') {
    return {
      clave: 'cobro-previo',
      titulo: 'Cobro previo',
      estado: 'bloqueada',
      descripcion: `Cliente con pago aplazado${cp.aplazado_texto ? ` (${cp.aplazado_texto})` : ''}`,
      accion: null,
    };
  }
  const hoy = inicioDelDia(hoyEntrada || new Date());
  const vence = parsearFechaDia(cp.vence_el);
  const pendiente = formatoEur(cp.pendiente);
  let plazo = '';
  if (cp.vencido) {
    const dias = Number(cp.dias_vencido) || (vence ? diasEntre(vence, hoy) : 0);
    plazo = `VENCIDO hace ${plural(dias, 'día', 'días')}`;
  } else if (vence) {
    const faltan = diasEntre(hoy, vence);
    plazo = faltan === 0 ? `vence hoy, ${formatoDiaMes(vence)}` : `vence el ${formatoDiaMes(vence)} (faltan ${plural(faltan, 'día', 'días')})`;
  }
  let descripcion;
  // U-95: fecha guardada en UTC sin zona
  const solicitado = cp.solicitado_en ? parsearFechaServidor(cp.solicitado_en) : null;
  if (solicitado) {
    descripcion = `Solicitado el ${formatoDiaMes(solicitado)}${cp.solicitado_por ? ` por ${cp.solicitado_por}` : ''}, pendiente ${pendiente}${plazo ? ` · ${plazo}` : ''}`;
  } else if (cp.vencido) {
    descripcion = `${plazo} · pendiente ${pendiente}`;
  } else {
    descripcion = `Pendiente ${pendiente}${plazo ? ` · ${plazo}` : ''}`;
  }
  // Multievento: cuánto es de las jornadas y cuánto de los suplementos del evento
  const detalleImporte = textoDetalleImporteCobroPrevio(cp);
  if (detalleImporte) descripcion = `${descripcion} · ${detalleImporte}`;
  const diasEvento = Number.isFinite(Number(cp.dias_para_evento)) ? Number(cp.dias_para_evento) : null;
  const puedePedir = !!cp.vencido || (diasEvento !== null && diasEvento <= DIAS_PEDIR_COBRO_PREVIO);
  const verCobros = { tipo: 'ir_facturacion', etiqueta: 'Ver cobros' };
  return {
    clave: 'cobro-previo',
    titulo: cp.vencido ? 'Cobro previo vencido' : 'Cobro previo',
    estado: 'pendiente',
    descripcion,
    accion: puedePedir ? { tipo: 'pedir_cobro_previo', etiqueta: solicitado ? 'Volver a pedir' : 'Pedir pago', principal: true } : verCobros,
    ...(puedePedir ? {
      acciones: [
        { tipo: 'pedir_cobro_previo', etiqueta: solicitado ? 'Volver a pedir' : 'Pedir pago', principal: true },
        verCobros,
      ],
    } : {}),
  };
};

const textoLimpio = (v) => {
  const t = String(v ?? '').trim();
  return ['none', 'null', 'pendiente', 'n/a'].includes(t.toLowerCase()) ? '' : t;
};

// Fin de una jornada: su día a la hora de fin (si acaba pasada la medianoche,
// al día siguiente); sin hora de fin, al terminar el día
const finDeJornada = (fecha, horaInicio, horaFin) => {
  if (!fecha) return null;
  const fin = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate(), 23, 59, 59);
  const m = /^(\d{1,2}):(\d{2})/.exec(String(horaFin || ''));
  if (!m) return fin;
  const res = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate(), Number(m[1]), Number(m[2]), 0);
  const ini = /^(\d{1,2}):(\d{2})/.exec(String(horaInicio || ''));
  if (ini && (Number(m[1]) * 60 + Number(m[2])) < (Number(ini[1]) * 60 + Number(ini[2]))) res.setDate(res.getDate() + 1);
  return res;
};

// Quién va de una jornada: lo del evento con lo propio de la jornada encima
// (mismo criterio que `quien_va_de_jornada` del backend)
export const quienVaDeJornada = (qv, jornadaId) => {
  if (!qv || typeof qv !== 'object') return null;
  const pj = qv.por_jornada && typeof qv.por_jornada === 'object' ? qv.por_jornada[jornadaId] : null;
  if (!pj || typeof pj !== 'object') return qv;
  const res = { ...qv };
  if (['propio', 'extra'].includes(pj.modo)) res.modo = pj.modo;
  if ('persona_id' in pj) {
    res.persona_id = pj.persona_id;
    res.persona_nombre = pj.persona_nombre;
  }
  return res;
};

/**
 * Datos de cada jornada de un multievento para "Por hacer" (vacío si el
 * presupuesto es de un día). Ordenadas por fecha.
 *
 * @param {object} p Presupuesto.
 * @param {object} [opciones] { materialAmbitos: `ambitos` del GET de material
 *   (uno por jornada, con jornada_id); facturasJornada: `facturas_jornada` del
 *   GET de facturas (si se pasa, una jornada está facturada solo si tiene una
 *   factura emitida, no un borrador; si no, la marca de la jornada) }.
 * @returns {Array<{ id, numero, fecha, fin, etiqueta, lugar, hereda,
 *   direccionConfirmada, facturada, numeroFactura, material, quienVa }>}
 *   `material` es null si no se ha cargado; `quienVa` es null salvo que exista
 *   `quien_va_evento.por_jornada`.
 */
// Una línea de menú de una jornada cuenta si tiene menú (menu_id) o nombre;
// las vacías no cuentan (mismo criterio que `jornadas_sin_lineas` del backend)
export const lineaJornadaConContenido = (m) => !!m && typeof m === 'object'
  && ['menu_id', 'nombre', 'nombre_menu'].some(c => String(m[c] ?? '').trim() !== '');

// ¿La jornada no tiene ninguna línea? Un multievento no puede tener jornadas
// vacías (decisión del usuario 05-oct-2026): el servidor no deja confirmar,
// enviar ni facturar hasta que se completen o se quiten
export const jornadaSinLineas = (j) => !(Array.isArray(j?.menus_personalizados)
  && j.menus_personalizados.some(lineaJornadaConContenido));

export const jornadasParaTareas = (p, opciones = {}) => {
  if (!p || !p.es_multi_evento || !Array.isArray(p.jornadas) || p.jornadas.length === 0) return [];
  const ambitos = Array.isArray(opciones.materialAmbitos) ? opciones.materialAmbitos : null;
  const facturas = Array.isArray(opciones.facturasJornada) ? opciones.facturasJornada : null;
  const facturaEmitidaDe = (jid) => (facturas || []).find(f => f && f.jornada_id === jid
    && !['borrador', 'anulada'].includes(String(f.estado || '').toLowerCase())) || null;
  const qv = p.quien_va_evento && typeof p.quien_va_evento === 'object' ? p.quien_va_evento : null;
  const porJornada = !!(qv && qv.por_jornada && typeof qv.por_jornada === 'object' && Object.keys(qv.por_jornada).length > 0);
  const lugarRaiz = textoLimpio(p.lugar_evento) || textoLimpio(p.direccion_exacta);
  const raizConfirmada = !!lugarRaiz && Number(p.distancia_km) > 0;
  return p.jornadas
    .filter(j => j && j.id)
    .map(j => ({ j, fecha: parsearFechaDia(j.fecha) }))
    .sort((a, b) => (a.fecha ? a.fecha.getTime() : Infinity) - (b.fecha ? b.fecha.getTime() : Infinity))
    .map(({ j, fecha }, i) => {
      const propio = textoLimpio(j.lugar_evento) || textoLimpio(j.direccion_exacta);
      const hereda = !propio || propio.toLowerCase() === lugarRaiz.toLowerCase();
      const amb = ambitos ? ambitos.find(a => a && a.jornada_id === j.id) : null;
      const gj = porJornada ? quienVaDeJornada(qv, j.id) : null;
      const factura = facturas ? facturaEmitidaDe(j.id) : null;
      return {
        id: j.id,
        numero: i + 1,
        fecha,
        fin: finDeJornada(fecha, j.hora_inicio, j.hora_fin),
        etiqueta: `Jornada ${i + 1}${fecha ? ` · ${formatoDiaMes(fecha)}` : ''}`,
        lugar: hereda ? lugarRaiz : propio,
        hereda,
        // Heredada: la de la dirección principal; propia: la suya con su ruta
        direccionConfirmada: hereda ? raizConfirmada : Number(j.distancia_km) > 0,
        facturada: facturas ? !!factura : !!(j.numero_factura || j.facturada),
        numeroFactura: facturas ? (factura?.numero_factura || null) : (j.numero_factura || null),
        material: amb ? { validado: !!amb.validado, huellaCambiada: !!amb.huella_cambiada, por: amb.por || null } : null,
        quienVa: gj ? { modo: gj.modo || null, personaNombre: textoLimpio(gj.persona_nombre) } : null,
        sinLineas: jornadaSinLineas(j),
      };
    });
};

// Subtareas de UNA jornada en la fase de producción
const tareasProduccionJornada = (jor, ctx) => {
  const { hoy, ahora, cerrado } = ctx;
  const grupo = { id: jor.id, numero: jor.numero, etiqueta: jor.etiqueta };
  const sufijo = ` · Jornada ${jor.numero}${jor.fecha ? ` (${formatoDiaMes(jor.fecha)})` : ''}`;
  const pasada = jor.fin ? ahora >= jor.fin : false;
  const tareas = [];

  tareas.push({
    clave: `direccion-j-${jor.id}`,
    titulo: `Dirección confirmada${sufijo}`,
    tituloEnGrupo: 'Dirección confirmada',
    estado: jor.direccionConfirmada ? 'hecha' : 'pendiente',
    descripcion: !jor.lugar
      ? 'Falta el lugar de la jornada'
      : (jor.direccionConfirmada
        ? `${jor.lugar}${jor.hereda ? ' (misma dirección principal)' : ''}`
        : (jor.hereda
          ? 'Misma dirección principal: elige la sugerencia de Google para calcular distancia y transporte'
          : 'Elige la sugerencia de Google para calcular distancia y transporte')),
    accion: jor.direccionConfirmada ? null : (jor.hereda
      ? { tipo: 'rapido', modal: 'direccion', etiqueta: 'Confirmar' }
      : { tipo: 'ir_logistica', etiqueta: 'Confirmar', jornadaId: jor.id }),
    jornada: grupo,
  });

  if (jor.material) {
    const m = jor.material;
    let t;
    if (m.huellaCambiada && !pasada) {
      t = { estado: 'pendiente', descripcion: 'Cambiaron las personas, los menús o los servicios después de validar el material', accion: { tipo: 'ir_material', etiqueta: 'Revisar', jornadaId: jor.id } };
    } else if (m.validado) {
      t = { estado: 'hecha', descripcion: `Material validado${m.por ? ` por ${m.por}` : ''}; la hoja sale con esa lista`, accion: null };
    } else if (pasada) {
      t = { estado: 'info', descripcion: 'La jornada ya se celebró sin validar el material', accion: { tipo: 'ir_material', etiqueta: 'Validar', jornadaId: jor.id } };
    } else {
      t = { estado: 'pendiente', descripcion: 'Revisa la lista y las unidades de esta jornada; al validar, su hoja de material sale con ella', accion: { tipo: 'ir_material', etiqueta: 'Validar', jornadaId: jor.id } };
    }
    tareas.push({ clave: `material-j-${jor.id}`, titulo: `Hoja de material${sufijo}`, tituloEnGrupo: 'Hoja de material', ...t, jornada: grupo });
  }

  if (jor.quienVa) {
    const q = jor.quienVa;
    let t;
    if (q.modo === 'extra') {
      t = { estado: 'hecha', descripcion: 'Va un responsable extra (en la solicitud de extras)', accion: null };
    } else if (q.personaNombre) {
      t = { estado: 'hecha', descripcion: `Va ${q.personaNombre}`, accion: null };
    } else {
      t = { estado: cerrado ? 'bloqueada' : 'pendiente', descripcion: 'Nadie elegido para esta jornada', accion: cerrado ? null : { tipo: 'ir_personal', etiqueta: 'Elegir' } };
    }
    tareas.push({ clave: `personal-j-${jor.id}`, titulo: `Personal${sufijo}`, tituloEnGrupo: 'Personal', ...t, jornada: grupo });
  }

  const fechaOrden = calcularFechaOrdenCocina(jor.fecha);
  let descripcionOrden = `Enviar ${HORAS_ORDEN_COCINA} h antes de la jornada`;
  if (fechaOrden) {
    descripcionOrden = !pasada && fechaOrden <= hoy
      ? `Toca enviarla: ${HORAS_ORDEN_COCINA} h antes era el ${formatoDiaMes(fechaOrden)}`
      : `Enviar ${HORAS_ORDEN_COCINA} h antes: ${formatoDiaMes(fechaOrden)}`;
  }
  tareas.push({
    clave: `orden-cocina-j-${jor.id}`,
    titulo: `Orden de cocina${sufijo}`,
    tituloEnGrupo: 'Orden de cocina',
    // No hay registro de envío de la orden: queda pendiente hasta la jornada
    estado: pasada ? 'hecha' : 'pendiente',
    descripcion: descripcionOrden,
    accion: { tipo: 'orden_cocina', etiqueta: 'Descargar orden de cocina', jornadaId: jor.id },
    // Orden nueva (motor de la lista de compra), en pruebas junto a la actual
    acciones: [
      { tipo: 'orden_cocina', etiqueta: 'Descargar orden de cocina', jornadaId: jor.id },
      { tipo: 'orden_cocina_nueva', etiqueta: 'Orden de cocina (nueva)', jornadaId: jor.id },
    ],
    nota: TEXTO_ORDEN_NUEVA_PRUEBAS,
    jornada: grupo,
  });
  return tareas;
};

// Factura de UNA jornada (facturación por jornada): se desbloquea cuando pasa
const tareaFacturaJornada = (jor, ctx) => {
  const { ahora, cerrado, confirmado, estado } = ctx;
  const pasada = jor.fin ? ahora >= jor.fin : false;
  let t;
  if (jor.facturada) {
    t = { estado: 'hecha', descripcion: jor.numeroFactura ? `Factura ${jor.numeroFactura} emitida` : 'Factura emitida', accion: null };
  } else if (cerrado) {
    t = { estado: 'bloqueada', descripcion: estado === 'cancelado' ? 'Evento cancelado' : 'Presupuesto rechazado', accion: null };
  } else if (!pasada) {
    t = { estado: 'bloqueada', descripcion: `Disponible tras la jornada${jor.fecha ? `, ${formatoDiaMes(jor.fecha)}` : ''}`, accion: null };
  } else if (!confirmado) {
    t = { estado: 'bloqueada', descripcion: 'Confirma el evento para emitir la factura de la jornada', accion: null };
  } else {
    t = { estado: 'pendiente', descripcion: 'La jornada ya se ha celebrado', accion: { tipo: 'emitir_factura_jornada', etiqueta: 'Emitir factura', principal: true, jornadaId: jor.id } };
  }
  return {
    clave: `factura-j-${jor.id}`,
    titulo: `Factura · Jornada ${jor.numero}${jor.fecha ? ` (${formatoDiaMes(jor.fecha)})` : ''}`,
    tituloEnGrupo: 'Factura',
    ...t,
    jornada: { id: jor.id, numero: jor.numero, etiqueta: jor.etiqueta },
  };
};

/**
 * Calcula las tareas del presupuesto agrupadas por fase.
 *
 * @param {object} d Datos ya preparados por la ficha:
 *   hoy, estado, fechaInicio, fechaFin, numMenus, numSuplementos, baseImponible,
 *   totalConIvaTexto, envios (registros_envios), bebidasConfirmadas,
 *   bebidasModo ('sin_alcohol' | 'con_alcohol' | 'personalizado'; null si no se
 *   ha cargado), bebidasModoValidadoEn (null = pendiente de validar),
 *   bebidasModoAplica (false si ningún menú lleva el plato de bebida mixto: no
 *   hay modo que validar; null si no se ha cargado), hayBebidasPersonalizadas
 *   (bebidas elegidas a mano en el presupuesto; con bebidasModoAplica false, la
 *   tarea solo aparece si las hay, con el criterio de siempre: confirmadas),
 *   coctel ([{ etiqueta, personas, recomendados, asignados, faltan }] o null si no
 *   se ha cargado), adaptaciones ({ total, personas, resumen }),
 *   alergenos ({ comensales, estado, estadoTexto, huellaCambiada, aviso,
 *   sinAlergicosPor, sinAlergicosEn } de la pestaña Alérgenos; opcional; con
 *   estado 'sin_alergicos' y 0 comensales la tarea queda hecha y no se avisa
 *   de nada más), observacionesTexto (observaciones generales y
 *   de las jornadas; si hablan de alergias o dietas sin nada registrado, la
 *   tarea de alérgenos queda pendiente), numPersonal,
 *   numFurgonetas, lugarEvento, distanciaKm,
 *   avisoRuta (texto de `avisoRutaPresupuesto`, o null: tarea "Calcular la
 *   ruta" en eventos no celebrados ni cancelados; opcional),
 *   sobrante ({ ingredientes, importe, importeTexto } o null),
 *   contactoFacturacion (bool), facturado (bool), numeroFactura, estadoPago,
 *   quienVa ({ modo, hayOrdenExtra, personasOrden, personasMenu } de "Quién va
 *   al evento"; opcional),
 *   horarios ({ aplica, servicios, sinHora, nombresSinHora, solapes: [{ clave, texto }] }
 *   de la hora por servicio, solo los solapes de montaje SIN decidir;
 *   opcional, solo formato nuevo),
 *   material ({ validado, validados, total, huellaCambiada, por } de la pestaña
 *   Material; opcional, solo formato nuevo),
 *   suplementoMinimo ({ minimo, pendientes: [{ clave, servicio, jornada, faltan,
 *   importeTexto }] } del GET reglas-comerciales: servicios con menos del mínimo
 *   sin su suplemento en un presupuesto ya enviado o confirmado, donde no se
 *   añade solo; opcional, solo formato nuevo),
 *   alquilerJornadas ([{ jornadaId, etiqueta, lineas, validado, texto }]:
 *   alquiler propio de cada jornada de un multievento, D10; opcional),
 *   alquiler ({ lineas, validado, validadoPor, huellaCambiada, estadoOrden
 *   (null sin orden | 'borrador' | 'enviada' | 'confirmada' | 'recogida' |
 *   'devuelta'), pagadoEn, fianzaDevueltaEn, proveedor, avisoFechas (texto si
 *   los días de alquiler superan los del evento y faltan por confirmar las
 *   fechas), cotizacion ({ estado: 'pendiente' | 'aplicada' | 'descartada' }
 *   de la cotización recibida del proveedor por correo), hayRecepcion (hay
 *   líneas de alquiler o menús "Es alquiler": algo que comprobar al llegar al
 *   evento), recepcionAplica (evento no celebrado o celebrado hoy: se puede
 *   anotar la recepción), recepcionComprobada, recepcionPor, recepcionEn y
 *   recepcionIncidencias (texto; con texto, tarea de incidencia hasta que se
 *   borre) } del GET de alquiler; solo si hay líneas de alquiler o menús de
 *   alquiler; opcional, solo formato nuevo),
 *   envio ({ modificado, cambios, fecha } del GET estado-envio: el presupuesto
 *   cambió después del último envío al cliente; opcional, solo formato nuevo),
 *   cobroPrevio (respuesta del GET cobro-previo: 100 % siete días antes del
 *   evento salvo clientes con pago aplazado; opcional, solo formato nuevo),
 *   jornadas (multievento: `jornadasParaTareas(presupuesto, ...)`; opcional),
 *   modoFacturacion (facturacion_multi_evento: 'por_jornada' saca una factura
 *   por jornada; opcional), facturacionJornadas ({ emitidas, total } en
 *   facturación por jornada: el evento está facturado solo con todas; opcional),
 *   compraResumen (`compra_resumen` del GET del presupuesto, o null si la lista
 *   de compra no tiene seguimiento; sin la clave no hay tarea "Hacer la compra")
 * @returns {{ fases, hechas, total, pendientes, siguienteHito, fechaLimiteCambios,
 *   fechaOrdenCocina, diasParaEvento, faseActual }}
 */
export const calcularTareasPorHacer = (d) => {
  const hoy = inicioDelDia(d.hoy || new Date());
  const cerrado = d.estado === 'cancelado' || d.estado === 'rechazado';
  const confirmado = d.estado === 'confirmado';
  const fechaInicio = d.fechaInicio || null;
  const fechaFin = d.fechaFin || fechaInicio;
  // U-96 (lote 7): un solo criterio de "celebrado", el del servidor y el de
  // `eventoPasado`: la última fecha del evento es anterior a hoy (el día del
  // evento todavía no cuenta como celebrado)
  const celebrado = !!fechaFin && fechaFin < hoy;
  const fechaLimiteCambios = calcularFechaLimiteCambios(fechaInicio);
  const fechaOrdenCocina = calcularFechaOrdenCocina(fechaInicio);
  const diasParaEvento = fechaInicio ? diasEntre(hoy, fechaInicio) : null;
  // Multievento: subtareas por jornada (un evento de un día no las tiene)
  const jornadas = Array.isArray(d.jornadas) ? d.jornadas.filter(j => j && j.id) : [];
  const porJornadas = jornadas.length > 0;
  const ctxJornadas = { hoy, ahora: d.ahora || new Date(), cerrado, confirmado, estado: d.estado };
  const tareasJornadas = porJornadas ? jornadas.map(j => tareasProduccionJornada(j, ctxJornadas)) : [];
  const conMaterialPorJornada = porJornadas && jornadas.some(j => j.material);
  const conPersonalPorJornada = porJornadas && jornadas.some(j => j.quienVa);

  // ----- Preparar y confirmar -----
  const preparar = [];
  const hayLineas = (d.numMenus || 0) + (d.numSuplementos || 0) > 0;
  preparar.push({
    clave: 'lineas',
    titulo: 'Líneas y precios',
    estado: hayLineas && d.baseImponible > 0 ? 'hecha' : 'pendiente',
    descripcion: hayLineas
      ? `${plural(d.numMenus || 0, 'menú', 'menús')} y ${plural(d.numSuplementos || 0, 'suplemento', 'suplementos')}${d.baseImponible > 0 ? '' : ' · sin importe'}`
      : 'Todavía no hay menús ni suplementos',
    accion: hayLineas && d.baseImponible > 0 ? null : { tipo: 'ir_lineas', etiqueta: 'Añadir líneas' },
  });

  // Multievento sin jornadas vacías (05-oct-2026): cada jornada sin líneas es
  // una tarea de prioridad alta; el servidor no deja confirmar, enviar ni
  // facturar mientras quede alguna. Evento celebrado o cerrado: no se toca.
  if (porJornadas && !cerrado && !celebrado) {
    jornadas.filter(j => j.sinLineas).forEach((j) => {
      preparar.push({
        clave: `jornada-sin-lineas-${j.id}`,
        titulo: `Jornada ${j.numero} sin líneas`,
        estado: 'pendiente',
        prioridad: 'alta',
        descripcion: `La jornada ${j.numero}${j.fecha ? ` (${formatoDiaMes(j.fecha)})` : ''} no tiene líneas: un multievento no puede tener jornadas vacías. Añade las líneas o quita la jornada.`,
        accion: { tipo: 'ir_lineas', etiqueta: 'Añadir líneas', principal: true },
      });
    });
  }

  // Suplemento hasta el mínimo por servicio: el que faltaría en un presupuesto
  // enviado o confirmado no se pone solo (no se cambia el precio que tiene el
  // cliente); queda como tarea con el botón Añadir
  const supMin = d.suplementoMinimo || null;
  if (supMin && !cerrado) {
    (supMin.pendientes || []).forEach((pend) => {
      preparar.push({
        clave: `suplemento-minimo-${pend.clave}`,
        titulo: `Falta el suplemento de mínimo ${supMin.minimo} en ${pend.servicio}${pend.jornada ? ` (${pend.jornada})` : ''}`,
        estado: 'pendiente',
        descripcion: `${plural(pend.faltan, 'persona', 'personas')} por debajo del mínimo: ${pend.importeTexto}`,
        accion: { tipo: 'suplemento_minimo', accion: 'anadir', clave: pend.clave, etiqueta: 'Añadir', principal: true },
      });
    });
  }

  // Hora extra del lunch (más de 1 h 30): en un presupuesto enviado o
  // confirmado no se pone sola; queda como tarea con el botón Añadir
  const horaExtra = d.horaExtraLunch || null;
  if (horaExtra && !cerrado && !celebrado) {
    (horaExtra.pendientes || []).forEach((pend) => {
      const minutos = Number(pend.minutosExtra) || 0;
      const exceso = minutos >= 60 ? `${Math.floor(minutos / 60)} h${minutos % 60 ? ` ${minutos % 60} min` : ''}` : `${minutos} min`;
      preparar.push({
        clave: `hora-extra-lunch-${pend.clave}`,
        titulo: `Falta la hora extra del lunch${pend.jornada ? ` (${pend.jornada})` : ''}`,
        estado: 'pendiente',
        descripcion: `Termina a las ${pend.horaFin}, ${exceso} más de la hora y media: ${pend.importeTexto}`,
        accion: { tipo: 'hora_extra_lunch', accion: 'anadir', clave: pend.clave, etiqueta: 'Añadir', principal: true },
      });
    });
  }

  // Hora por servicio (motor nuevo): sin ella no se confirma ni sale la orden de cocina
  const hor = d.horarios || null;
  if (hor && hor.aplica && (hor.servicios || 0) > 0) {
    const n = Number(hor.sinHora) || 0;
    preparar.push({
      clave: 'horas-servicios',
      titulo: n > 0 ? `Poner hora a ${plural(n, 'servicio', 'servicios')}` : 'Hora de los servicios',
      estado: n > 0 ? (cerrado ? 'bloqueada' : 'pendiente') : 'hecha',
      descripcion: n > 0
        ? `Sin hora: ${(hor.nombresSinHora || []).join(', ')}. Sin ella no se puede generar la orden de cocina`
        : `${plural(hor.servicios, 'servicio', 'servicios')} con hora`,
      accion: n > 0 && !cerrado ? { tipo: 'proponer_horas', etiqueta: 'Proponer horas', principal: true } : null,
    });
  }

  const envios = Array.isArray(d.envios) ? d.envios : [];
  const ultimoEnvio = envios.length > 0 ? envios[envios.length - 1] : null;
  // U-95: fecha guardada en UTC sin zona
  const fechaUltimoEnvio = ultimoEnvio?.fecha_envio ? parsearFechaServidor(ultimoEnvio.fecha_envio) : null;
  const enviado = envios.length > 0 || ['enviado', 'aceptado', 'confirmado'].includes(d.estado);
  let descripcionEnvio = 'Aún no se ha enviado al cliente';
  if (ultimoEnvio) {
    const destino = ultimoEnvio.contacto_destino_nombre || ultimoEnvio.email_destino;
    descripcionEnvio = `${plural(envios.length, 'envío', 'envíos')}; el último${fechaUltimoEnvio && !Number.isNaN(fechaUltimoEnvio.getTime()) ? ` el ${formatoDiaMes(fechaUltimoEnvio)}` : ''}${destino ? ` a ${destino}` : ''}`;
  } else if (enviado) {
    descripcionEnvio = 'Marcado como enviado (sin envío registrado desde la ficha)';
  }
  preparar.push({
    clave: 'enviado',
    titulo: 'Presupuesto enviado',
    estado: enviado ? 'hecha' : (cerrado ? 'bloqueada' : 'pendiente'),
    descripcion: descripcionEnvio,
    accion: enviado || cerrado ? null : { tipo: 'enviar_presupuesto', etiqueta: 'Enviar presupuesto', principal: true },
  });

  // Ruta desde la sede sin calcular (U-02): condiciona el tiempo del personal
  // y el gasoil. Solo en eventos no celebrados ni cancelados.
  if (d.avisoRuta && !cerrado && !celebrado) {
    preparar.push({
      clave: 'ruta',
      titulo: 'Calcular la ruta desde la nave',
      estado: 'pendiente',
      descripcion: d.avisoRuta,
      accion: { tipo: 'calcular_ruta', etiqueta: 'Calcular ruta', principal: true },
    });
  }

  // Hora de fin sin definir: se calcula con la duración típica; que la ponga
  // quien lleve el evento (Alfonso) cuando la sepa
  if (d.avisoHoraFin && !cerrado && !celebrado) {
    preparar.push({
      clave: 'hora_fin',
      titulo: 'Confirmar la hora de fin del evento (provisional)',
      estado: 'pendiente',
      descripcion: d.avisoHoraFin,
      accion: null,
    });
  }

  // Cambió lo comercial (personas, fecha, líneas, totales...) después del
  // último envío: el cliente tiene una versión vieja
  if (d.envio && d.envio.modificado && !cerrado && !celebrado) {
    preparar.push(tareaReenviar(d.envio));
  }

  preparar.push({
    clave: 'confirmado',
    titulo: 'Evento confirmado',
    estado: confirmado ? 'hecha' : (cerrado ? 'bloqueada' : 'pendiente'),
    descripcion: confirmado
      ? 'El cliente ha confirmado el evento'
      : (cerrado ? (d.estado === 'cancelado' ? 'Evento cancelado' : 'Presupuesto rechazado') : 'Pendiente de confirmación del cliente'),
    accion: confirmado || cerrado ? null : { tipo: 'confirmar', etiqueta: 'Confirmar evento', principal: enviado },
  });

  // Cobro previo: con el evento confirmado y antes de celebrarse
  const cobroPrevio = confirmado && !celebrado ? tareaCobroPrevio(d.cobroPrevio, hoy) : null;
  if (cobroPrevio) preparar.push(cobroPrevio);

  // ----- Producir el evento -----
  const producir = [];
  // Confirmado y cambiado después (fecha, personas o líneas): el stock, los
  // lotes y el material siguen como se reservaron al confirmar (U-09)
  if (confirmado && !celebrado && d.confirmadoModificado) {
    producir.push({
      clave: 'confirmado-modificado',
      titulo: 'Rehacer stock, lotes y material',
      estado: 'pendiente',
      descripcion: 'Has cambiado el evento después de confirmarlo: el stock, los lotes y el material siguen reservados como estaban. Desconfirma y vuelve a confirmar para rehacerlos.',
      accion: { tipo: 'desconfirmar', etiqueta: 'Desconfirmar' },
    });
  }
  // Bebidas: con el modo cargado (formato nuevo), hay que validarlo (variado sin
  // alcohol, con alcohol o personalizado) y, en personalizado, confirmar las
  // bebidas por menú. Sin el modo, el criterio de siempre (confirmadas).
  // Si el modo no aplica (ningún menú lleva el plato de bebida mixto), no hay
  // tarea de bebidas salvo que haya bebidas elegidas a mano: entonces, confirmarlas.
  if (d.bebidasModoAplica === false) {
    if (d.hayBebidasPersonalizadas) {
      producir.push({
        clave: 'bebidas',
        titulo: 'Bebidas confirmadas',
        estado: d.bebidasConfirmadas ? 'hecha' : 'pendiente',
        descripcion: d.bebidasConfirmadas ? 'Bebidas revisadas y confirmadas' : 'Faltan por definir o confirmar las bebidas',
        accion: d.bebidasConfirmadas ? null : { tipo: 'rapido', modal: 'bebidas', etiqueta: 'Definir bebidas' },
      });
    }
  } else if (d.bebidasModo) {
    const sinValidar = !d.bebidasModoValidadoEn;
    const personalizadoSinConfirmar = d.bebidasModo === 'personalizado' && !d.bebidasConfirmadas;
    let tareaBebidas;
    if (sinValidar) {
      tareaBebidas = {
        titulo: 'Validar bebidas',
        estado: 'pendiente',
        descripcion: 'Elige el modo de bebidas: variado sin alcohol, variado con alcohol o personalizado',
        accion: { tipo: 'rapido', modal: 'bebidas', etiqueta: 'Validar' },
      };
    } else if (personalizadoSinConfirmar) {
      tareaBebidas = {
        titulo: 'Confirmar bebidas personalizadas',
        estado: 'pendiente',
        descripcion: 'Modo personalizado: faltan por elegir o confirmar las bebidas de cada menú',
        accion: { tipo: 'rapido', modal: 'bebidas', etiqueta: 'Confirmar' },
      };
    } else {
      tareaBebidas = {
        titulo: 'Bebidas validadas',
        estado: 'hecha',
        descripcion: d.bebidasModo === 'personalizado'
          ? 'Bebidas personalizadas confirmadas'
          : (d.bebidasModo === 'con_alcohol' ? 'Variado con alcohol' : 'Variado sin alcohol'),
        accion: null,
      };
    }
    producir.push({ clave: 'bebidas', ...tareaBebidas });
  } else {
    producir.push({
      clave: 'bebidas',
      titulo: 'Bebidas confirmadas',
      estado: d.bebidasConfirmadas ? 'hecha' : 'pendiente',
      descripcion: d.bebidasConfirmadas ? 'Bebidas revisadas y confirmadas' : 'Faltan por definir o confirmar las bebidas',
      accion: d.bebidasConfirmadas ? null : { tipo: 'rapido', modal: 'bebidas', etiqueta: 'Definir bebidas' },
    });
  }

  // Quién va al evento (4.5): hace falta personal externo si el responsable
  // del montaje es un extra o si los ratios del menú piden camareros o extras.
  // En ese caso la tarea "Rellenar la solicitud de extras" sustituye a las de
  // camareros en cóctel (regla 1.4): las dos se resuelven con la misma orden de
  // personal extra y el aviso de "faltan" pasa a esa tarea.
  const qv = d.quienVa || null;
  const personasMenu = Number(qv?.personasMenu) || 0;
  const necesitaExtras = !!qv && (qv.modo === 'extra' || personasMenu > 0);
  const coctelFaltan = (d.coctel || []).filter(c => (Number(c.faltan) || 0) > 0);

  (necesitaExtras ? [] : (d.coctel || [])).forEach((c, i) => {
    const faltan = Number(c.faltan) || 0;
    producir.push({
      clave: `coctel-${i}`,
      titulo: `Camareros en cóctel${c.etiqueta ? ` · ${c.etiqueta}` : ''}`,
      estado: faltan > 0 ? 'pendiente' : 'hecha',
      descripcion: `Cóctel de ${c.personas} personas: se recomiendan ${c.recomendados}, hay ${c.asignados}`,
      accion: faltan > 0 ? { tipo: 'rapido', modal: 'personal', etiqueta: `Faltan ${faltan}` } : null,
    });
  });

  // Pestaña Alérgenos: validar con cocina cuando hay comensales (con alergias
  // o con dietas; `comensales` son personas: una fila puede ser "5 vegetarianos")
  const alerg = d.alergenos || { comensales: 0 };
  if (alerg.comensales > 0) {
    const validada = alerg.estado === 'validada_cocina';
    const nComensales = plural(alerg.comensales, 'comensal con alergias o dietas', 'comensales con alergias o dietas');
    let tareaAlergenos;
    if (validada && alerg.huellaCambiada && !celebrado) {
      tareaAlergenos = {
        titulo: 'Alérgenos y dietas: revisar tras los cambios',
        estado: 'pendiente',
        descripcion: `Cambiaron las personas o los menús después de validar con cocina (${nComensales})`,
        accion: { tipo: 'rapido', modal: 'alergenos', etiqueta: 'Revisar' },
      };
    } else if (validada || celebrado) {
      tareaAlergenos = {
        titulo: 'Alérgenos y dietas validados con cocina',
        estado: 'hecha',
        descripcion: nComensales,
        accion: null,
      };
    } else {
      tareaAlergenos = {
        titulo: 'Alérgenos y dietas: validar con cocina',
        estado: 'pendiente',
        descripcion: `${nComensales} · ${alerg.estado === 'recibida' ? 'lista definitiva' : 'lista pendiente del cliente'}${alerg.aviso ? `. ${alerg.aviso}` : ''}`,
        accion: { tipo: 'rapido', modal: 'alergenos', etiqueta: 'Validar' },
      };
    }
    producir.push({ clave: 'alergenos', ...tareaAlergenos });
  }
  // El cliente confirmó que no hay alérgicos: tarea hecha y sin más avisos
  const sinAlergicos = !(alerg.comensales > 0) && alerg.estado === 'sin_alergicos';
  if (sinAlergicos) {
    const por = alerg.sinAlergicosPor ? ` por ${alerg.sinAlergicosPor}` : '';
    const en = formatoFechaHoraUtc(alerg.sinAlergicosEn);
    producir.push({
      clave: 'alergenos',
      titulo: 'Sin alérgicos ni dietas',
      estado: 'hecha',
      descripcion: `Confirmado${por}${en ? ` el ${en}` : ''}`,
      accion: null,
    });
  }

  const adapt = d.adaptaciones || { total: 0 };
  if (adapt.total > 0) {
    producir.push({
      clave: 'adaptaciones',
      titulo: 'Revisar adaptaciones',
      estado: celebrado ? 'hecha' : 'pendiente',
      descripcion: adapt.resumen || plural(adapt.total, 'adaptación', 'adaptaciones'),
      accion: celebrado ? null : { tipo: 'ir_lineas', etiqueta: 'Revisar' },
    });
  } else if (!(alerg.comensales > 0) && !sinAlergicos) {
    if (mencionaAlergiaODieta(d.observacionesTexto) && !celebrado) {
      // Las observaciones hablan de alergias o dietas pero no hay nada registrado
      producir.push({
        clave: 'alergenos',
        titulo: 'Alérgenos y dietas',
        estado: 'pendiente',
        descripcion: TEXTO_ALERGIA_SIN_REGISTRAR,
        accion: null,
        acciones: [
          { tipo: 'rapido', modal: 'alergenos', etiqueta: 'Registrar', principal: true },
          { tipo: 'sin_alergicos', etiqueta: 'No hay alérgicos ni dietas' },
        ],
      });
    } else {
      // Sin alergias comunicadas: botón para dejarlo confirmado (tarea hecha
      // "Sin alérgicos"); en eventos celebrados o cerrados solo se informa
      const puedeMarcar = !celebrado && !cerrado;
      producir.push({
        clave: 'adaptaciones',
        titulo: 'Sin alergias ni dietas comunicadas',
        estado: 'info',
        descripcion: puedeMarcar
          ? 'Si el cliente confirma que no hay alérgicos ni dietas especiales (o no los comunica), márcalo y la tarea queda hecha'
          : 'No hay adaptaciones dietéticas en los menús',
        accion: puedeMarcar ? { tipo: 'sin_alergicos', etiqueta: 'No hay alérgicos ni dietas' } : null,
      });
    }
  }

  if (fechaLimiteCambios) {
    const abierto = hoy <= fechaLimiteCambios;
    producir.push({
      clave: 'limite-cambios',
      titulo: `Fecha límite de cambios: ${formatoDiaMes(fechaLimiteCambios)}`,
      estado: abierto ? 'pendiente' : 'hecha',
      descripcion: abierto
        ? `Hasta ese día (${DIAS_LIMITE_CAMBIOS} días antes) el cliente confirma invitados, alergias y cambios`
        : `Plazo cerrado; los cambios desde el ${formatoDiaMes(sumarDias(fechaLimiteCambios, 1))} van fuera de plazo`,
      accion: null,
    });
  }

  if (!conPersonalPorJornada) producir.push({
    clave: 'personal',
    titulo: 'Personal asignado',
    estado: d.numPersonal > 0 ? 'hecha' : 'pendiente',
    descripcion: d.numPersonal > 0 ? plural(d.numPersonal, 'persona asignada', 'personas asignadas') : 'Nadie asignado todavía',
    accion: d.numPersonal > 0 ? null : { tipo: 'rapido', modal: 'personal', etiqueta: 'Asignar' },
  });

  // Hecha cuando existe la orden de personal extra y, si hay cóctel, cubre los
  // camareros recomendados (1.4).
  if (necesitaExtras) {
    const hayOrden = !!qv.hayOrdenExtra;
    const partes = [];
    if (qv.modo === 'extra') partes.push('responsable del montaje');
    if (personasMenu > 0) partes.push(`${plural(personasMenu, 'persona', 'personas')} del menú`);
    const externos = personasMenu + (qv.modo === 'extra' ? 1 : 0);
    const textoCoctel = coctelFaltan
      .map(c => `en cóctel${c.etiqueta ? ` (${c.etiqueta})` : ''} se recomiendan ${c.recomendados} camareros y hay ${c.asignados}`)
      .join('; ');
    let estado = 'pendiente';
    let descripcion = `Personal externo: ${plural(externos, 'persona', 'personas')} (${partes.join(' y ')})`;
    let accion = { tipo: 'rapido', modal: 'solicitud_extras', etiqueta: 'Rellenar', principal: true };
    if (hayOrden && coctelFaltan.length === 0) {
      estado = 'hecha';
      descripcion = `Orden de personal extra creada${qv.personasOrden ? ` (${plural(qv.personasOrden, 'persona', 'personas')})` : ''}`;
      accion = null;
    } else if (hayOrden) {
      descripcion = `Orden creada, pero ${textoCoctel}`;
      accion = { tipo: 'rapido', modal: 'solicitud_extras', etiqueta: 'Revisar' };
    } else if (textoCoctel) {
      descripcion = `${descripcion}; ${textoCoctel}`;
    }
    producir.push({ clave: 'solicitud-extras', titulo: 'Rellenar la solicitud de extras', estado, descripcion, accion });
  }

  // Solape de montaje (un montaje intermedio no cabe entre dos servicios):
  // solo AVISO informativo mientras no se decida, con "Añadir 1 extra" o
  // "Ignorar". Aceptado, el extra entra en "Rellenar la solicitud de extras".
  const solapes = (hor && hor.aplica && Array.isArray(hor.solapes) && !celebrado && !cerrado) ? hor.solapes : [];
  solapes.forEach((sol, i) => {
    if (!sol || !sol.clave) return;
    producir.push({
      clave: i === 0 ? 'solape-montaje' : `solape-montaje-${i + 1}`,
      titulo: 'Solape de montaje',
      estado: 'info',
      descripcion: sol.texto,
      accion: { tipo: 'solape', clave: sol.clave, decision: 'extra', etiqueta: 'Añadir 1 extra', principal: true },
      acciones: [
        { tipo: 'solape', clave: sol.clave, decision: 'extra', etiqueta: 'Añadir 1 extra', principal: true },
        { tipo: 'solape', clave: sol.clave, decision: 'ignorado', etiqueta: 'Ignorar' },
      ],
    });
  });

  producir.push({
    clave: 'furgonetas',
    titulo: 'Furgonetas',
    estado: d.numFurgonetas > 0 ? 'hecha' : 'pendiente',
    descripcion: d.numFurgonetas > 0 ? plural(d.numFurgonetas, 'furgoneta asignada', 'furgonetas asignadas') : 'Sin furgonetas asignadas',
    accion: d.numFurgonetas > 0 ? null : { tipo: 'rapido', modal: 'furgonetas', etiqueta: 'Asignar' },
  });

  // Mismo criterio que el aviso "Confirmar dirección" de Logística
  const direccionConfirmada = !!d.lugarEvento && !!d.distanciaKm;
  if (!porJornadas) producir.push({
    clave: 'direccion',
    titulo: 'Dirección del evento confirmada',
    estado: direccionConfirmada ? 'hecha' : 'pendiente',
    descripcion: !d.lugarEvento
      ? 'Falta el lugar del evento'
      : (direccionConfirmada ? d.lugarEvento : 'Elige la sugerencia de Google para calcular distancia y transporte'),
    accion: direccionConfirmada ? null : { tipo: 'rapido', modal: 'direccion', etiqueta: 'Confirmar' },
  });

  // Pestaña Material: validar la lista (la hoja de material sale con ella).
  // Vuelve a pendiente si cambian personas, menús o servicios tras validar.
  const mat = d.material;
  if (mat && mat.total > 0 && !conMaterialPorJornada) {
    const multi = mat.total > 1;
    let tareaMaterial;
    if (mat.huellaCambiada && !celebrado) {
      tareaMaterial = {
        titulo: 'Material: revisar tras los cambios',
        estado: 'pendiente',
        descripcion: 'Cambiaron las personas, los menús o los servicios después de validar el material',
        accion: { tipo: 'ir_material', etiqueta: 'Revisar' },
      };
    } else if (mat.validado) {
      tareaMaterial = {
        titulo: 'Material validado',
        estado: 'hecha',
        descripcion: multi ? `${plural(mat.total, 'jornada validada', 'jornadas validadas')}` : `Validado${mat.por ? ` por ${mat.por}` : ''}; la hoja de material sale con esa lista`,
        accion: null,
      };
    } else if (celebrado) {
      // Evento pasado sin validar: nunca se da por validado; queda como aviso
      tareaMaterial = {
        titulo: 'Material sin validar',
        estado: 'info',
        descripcion: 'El evento ya se celebró; valida la lista si quieres dejarla cerrada',
        accion: { tipo: 'ir_material', etiqueta: 'Validar' },
      };
    } else {
      tareaMaterial = {
        titulo: 'Validar el material',
        estado: 'pendiente',
        descripcion: multi
          ? `${mat.validados || 0} de ${plural(mat.total, 'jornada validada', 'jornadas validadas')}`
          : 'Revisa la lista y las unidades; al validar, la hoja de material sale con ella',
        accion: { tipo: 'ir_material', etiqueta: 'Validar' },
      };
    }
    producir.push({ clave: 'material', ...tareaMaterial });
  }

  // Lista de compra: "Hacer la compra" (confirmado futuro, con compraResumen)
  const tCompra = tareaCompra(d.compraResumen, { hoy, fechaInicio, confirmado, celebrado, cerrado });
  if (tCompra) producir.push(tCompra);
  const tPedidosDias = tareaPedidosEnSusDias(d.compraResumen, { confirmado, celebrado, cerrado });
  if (tPedidosDias) producir.push(tPedidosDias);

  // Alquiler de material: validar, pedir al proveedor, confirmar su
  // cotización, pagar antes del evento y, tras el evento, recuperar la fianza
  const alq = d.alquiler;
  if (alq && (alq.lineas || 0) > 0) {
    // Abre el panel de alquiler de la pestaña Material (plegado por defecto)
    const accionAlquiler = (etiqueta, principal = false) => ({ tipo: 'rapido', modal: 'alquiler', etiqueta, ...(principal ? { principal: true } : {}) });
    const estadoOrden = alq.estadoOrden || null;
    const ordenEnviada = !!estadoOrden && estadoOrden !== 'borrador';
    const cotizacionConfirmada = ['confirmada', 'recogida', 'devuelta'].includes(estadoOrden);
    const proveedorTexto = alq.proveedor ? ` a ${alq.proveedor}` : ' al proveedor';

    const alquilerValidado = alq.validado && !alq.huellaCambiada;
    let descripcionValidar = `${plural(alq.lineas, 'artículo', 'artículos')} de alquiler sin validar`;
    if (alq.validado && alq.huellaCambiada) descripcionValidar = 'Cambió el evento después de validar el alquiler: revisa las cantidades';
    else if (alquilerValidado) descripcionValidar = `${plural(alq.lineas, 'artículo', 'artículos')}${alq.validadoPor ? ` · validado por ${alq.validadoPor}` : ''}`;
    producir.push({
      clave: 'alquiler-validar',
      titulo: alquilerValidado ? 'Alquiler validado' : 'Validar alquiler',
      estado: alquilerValidado ? 'hecha' : 'pendiente',
      descripcion: descripcionValidar,
      accion: alquilerValidado ? null : accionAlquiler(alq.validado ? 'Revisar' : 'Validar'),
    });

    // Con más días de alquiler que de evento, antes de enviar hay que confirmar
    // las fechas (no sabemos si el día de más va antes o después)
    const faltanFechas = !ordenEnviada && !!alq.avisoFechas;
    producir.push({
      clave: 'alquiler-orden',
      titulo: faltanFechas ? 'Confirmar fechas de la orden de alquiler' : 'Enviar orden de alquiler',
      estado: ordenEnviada ? 'hecha' : 'pendiente',
      descripcion: ordenEnviada
        ? `Orden enviada${proveedorTexto}`
        : (faltanFechas
          ? alq.avisoFechas
          : (estadoOrden === 'borrador' ? 'La orden está en borrador' : `Prepara la orden de pedido y envíala${proveedorTexto}`)),
      accion: ordenEnviada ? null : accionAlquiler(faltanFechas ? 'Confirmar fechas' : (estadoOrden === 'borrador' ? 'Enviar' : 'Preparar')),
    });

    // Cotización recibida por correo (PDF del proveedor leído): revisarla
    const estadoCotRecibida = alq.cotizacion?.estado || null;
    if (estadoCotRecibida) {
      const pendienteRevisar = estadoCotRecibida === 'pendiente';
      producir.push({
        clave: 'alquiler-cotizacion-recibida',
        titulo: `Revisar cotización de ${alq.proveedor || 'Alea'}`,
        estado: pendienteRevisar ? 'pendiente' : 'hecha',
        descripcion: pendienteRevisar
          ? 'Ha llegado su presupuesto por correo: compáralo y aplícalo como coste real'
          : (estadoCotRecibida === 'aplicada' ? 'Cotización aplicada como coste real' : 'Cotización descartada'),
        accion: pendienteRevisar ? accionAlquiler('Revisar', true) : null,
      });
    }

    let estadoCotizacion = 'bloqueada';
    let descripcionCotizacion = 'Tras enviar la orden, con el presupuesto del proveedor';
    if (cotizacionConfirmada) {
      estadoCotizacion = 'hecha';
      descripcionCotizacion = 'Coste real del alquiler anotado';
    } else if (estadoOrden === 'enviada') {
      estadoCotizacion = 'pendiente';
      descripcionCotizacion = 'Anota el presupuesto del proveedor (material, transporte, lavado)';
    }
    producir.push({
      clave: 'alquiler-cotizacion',
      titulo: 'Confirmar cotización del proveedor',
      estado: estadoCotizacion,
      descripcion: descripcionCotizacion,
      accion: estadoCotizacion === 'pendiente' ? accionAlquiler('Confirmar') : null,
    });

    const fechaPago = fechaInicio ? sumarDias(fechaInicio, -DIAS_PAGO_ALQUILER) : null;
    let estadoPagoAlquiler = 'bloqueada';
    let descripcionPago = 'Tras confirmar la cotización del proveedor';
    if (alq.pagadoEn) {
      estadoPagoAlquiler = 'hecha';
      const fPago = parsearFechaDia(alq.pagadoEn);
      descripcionPago = fPago ? `Pagado el ${formatoDiaMes(fPago)}` : 'Pagado';
    } else if (cotizacionConfirmada) {
      estadoPagoAlquiler = 'pendiente';
      descripcionPago = fechaPago && fechaPago < hoy
        ? 'Fuera de plazo: el proveedor cobra antes del evento'
        : 'El proveedor cobra el alquiler y la fianza antes del evento';
    }
    producir.push({
      clave: 'alquiler-pago',
      titulo: fechaPago ? `Pagar alquiler antes del ${formatoDiaMes(fechaPago)}` : 'Pagar alquiler',
      estado: estadoPagoAlquiler,
      descripcion: descripcionPago,
      accion: estadoPagoAlquiler === 'pendiente' ? accionAlquiler('Marcar pagado') : null,
    });

    if (celebrado) {
      const devuelta = estadoOrden === 'devuelta';
      const fDev = parsearFechaDia(alq.fianzaDevueltaEn);
      producir.push({
        clave: 'alquiler-fianza',
        titulo: 'Devolución de fianza',
        estado: devuelta ? 'hecha' : 'pendiente',
        descripcion: devuelta
          ? (fDev ? `Material devuelto; fianza devuelta el ${formatoDiaMes(fDev)}` : 'Material devuelto')
          : 'Comprueba que el proveedor ha recogido el material y devuelto la fianza',
        accion: devuelta ? null : accionAlquiler('Revisar'),
      });
    }
  }

  // Multievento (D10): alquiler propio de cada jornada (de sus
  // `materiales_alquiler`, sin más peticiones): "Jornada 2: 3 líneas, sin validar"
  (Array.isArray(d.alquilerJornadas) ? d.alquilerJornadas : []).forEach((r) => {
    producir.push({
      clave: `alquiler-validar-${r.jornadaId}`,
      titulo: r.validado ? `Alquiler validado · ${r.etiqueta}` : `Validar alquiler · ${r.etiqueta}`,
      estado: r.validado ? 'hecha' : 'pendiente',
      descripcion: r.texto,
      accion: r.validado ? null : { tipo: 'rapido', modal: 'alquiler', etiqueta: 'Validar' },
    });
  });

  // Recepción del alquiler en el evento (se comprueba en la hoja de material
  // impresa): desde el día del evento mientras se pueda anotar; hecha al marcar
  // "Recepción comprobada". Las incidencias anotadas, aparte, hasta borrarlas.
  if (alq && alq.hayRecepcion) {
    const accionRecepcion = (etiqueta, principal = false) => ({ tipo: 'rapido', modal: 'alquiler', etiqueta, ...(principal ? { principal: true } : {}) });
    const diaDelEvento = !!fechaInicio && fechaInicio <= hoy;
    if (alq.recepcionComprobada || (alq.recepcionAplica && diaDelEvento)) {
      const fCompr = parsearFechaDia(alq.recepcionEn);
      producir.push({
        clave: 'alquiler-recepcion',
        titulo: 'Comprobar la recepción del alquiler (hoja de material)',
        estado: alq.recepcionComprobada ? 'hecha' : 'pendiente',
        descripcion: alq.recepcionComprobada
          ? `Recepción comprobada${alq.recepcionPor ? ` por ${alq.recepcionPor}` : ''}${fCompr ? ` el ${formatoDiaMes(fCompr)}` : ''}`
          : 'Comprueba en la hoja de material lo que ha llegado y márcalo como comprobado',
        accion: alq.recepcionComprobada ? null : accionRecepcion('Comprobar', true),
      });
    }
    const incidencias = String(alq.recepcionIncidencias || '').trim();
    if (incidencias) {
      producir.push({
        clave: 'alquiler-recepcion-incidencia',
        titulo: `Incidencia en la recepción del alquiler: ${incidencias}`,
        estado: 'pendiente',
        descripcion: 'Revísala con el proveedor; se cierra al borrar el texto de incidencias',
        accion: accionRecepcion('Revisar'),
      });
    }
  }

  if (!porJornadas) producir.push({
    clave: 'orden-cocina',
    titulo: 'Orden de cocina',
    // No hay registro de envío de la orden: queda pendiente hasta el evento
    estado: celebrado ? 'hecha' : 'pendiente',
    descripcion: fechaOrdenCocina
      ? `Enviar ${HORAS_ORDEN_COCINA} h antes: ${formatoDiaMes(fechaOrdenCocina)}`
      : `Enviar ${HORAS_ORDEN_COCINA} h antes del evento`,
    accion: { tipo: 'orden_cocina', etiqueta: 'Descargar orden de cocina' },
    // Orden nueva (motor de la lista de compra), en pruebas junto a la actual
    acciones: [
      { tipo: 'orden_cocina', etiqueta: 'Descargar orden de cocina' },
      { tipo: 'orden_cocina_nueva', etiqueta: 'Orden de cocina (nueva)' },
    ],
    nota: TEXTO_ORDEN_NUEVA_PRUEBAS,
  });

  // Multievento: las subtareas de cada jornada, agrupadas y en orden de fecha
  tareasJornadas.forEach(lista => producir.push(...lista));

  // Sobrante estimado: OCULTO a petición del usuario (30-sep-2026) hasta afinar el cálculo.
  if (false && d.sobrante && d.sobrante.importe > 0) {
    producir.push({
      clave: 'sobrante',
      titulo: 'Sobrante estimado',
      estado: 'info',
      descripcion: `${plural(d.sobrante.ingredientes, 'ingrediente', 'ingredientes')} por valor de ${d.sobrante.importeTexto || d.sobrante.importe}; se puede regalar al cliente`,
      accion: { tipo: 'ir_lineas', etiqueta: 'Ver sobrante' },
    });
  }

  // ----- Facturar y cobrar -----
  const facturar = [];
  facturar.push({
    clave: 'contacto-facturacion',
    titulo: 'Contacto de facturación',
    estado: d.contactoFacturacion ? 'hecha' : 'pendiente',
    descripcion: d.contactoFacturacion ? 'Asignado' : 'Se puede hacer ya; lo pide la factura final',
    accion: d.contactoFacturacion ? null : { tipo: 'rapido', modal: 'contacto_facturacion', etiqueta: 'Asignar' },
  });

  let estadoFactura;
  let descripcionFactura;
  let accionFactura = null;
  if (d.facturado) {
    estadoFactura = 'hecha';
    descripcionFactura = d.numeroFactura ? `Factura ${d.numeroFactura} emitida` : 'Factura emitida';
  } else if (cerrado) {
    estadoFactura = 'bloqueada';
    descripcionFactura = d.estado === 'cancelado' ? 'Evento cancelado' : 'Presupuesto rechazado';
  } else if (!celebrado) {
    estadoFactura = 'bloqueada';
    descripcionFactura = `Disponible tras el evento${fechaFin ? `, ${formatoDiaMes(fechaFin)}` : ''}${d.totalConIvaTexto ? ` · ${d.totalConIvaTexto}` : ''}`;
  } else {
    estadoFactura = 'pendiente';
    descripcionFactura = 'El evento ya se ha celebrado';
    accionFactura = { tipo: 'emitir_factura', etiqueta: 'Emitir factura', principal: true };
  }
  if (porJornadas && d.modoFacturacion === 'por_jornada') {
    // Una factura por jornada: cada una se desbloquea cuando pasa su jornada
    jornadas.forEach(j => facturar.push(tareaFacturaJornada(j, ctxJornadas)));
  } else {
    facturar.push({ clave: 'factura', titulo: 'Factura final', estado: estadoFactura, descripcion: descripcionFactura, accion: accionFactura });
  }

  const pago = String(d.estadoPago || '').toLowerCase();
  let estadoCobro;
  let descripcionCobro;
  if (['pagado', 'si'].includes(pago)) {
    estadoCobro = 'hecha';
    descripcionCobro = 'Cobrado';
  } else if (!d.facturado && d.facturacionJornadas && d.facturacionJornadas.emitidas > 0) {
    // Facturación por jornada a medias: lo facturado ya se puede cobrar
    estadoCobro = 'pendiente';
    descripcionCobro = `Facturado parcialmente: ${d.facturacionJornadas.emitidas} de ${d.facturacionJornadas.total} jornadas`;
  } else if (!d.facturado) {
    estadoCobro = 'bloqueada';
    descripcionCobro = 'Pendiente de emitir la factura';
  } else if (['parcial', 'pago_parcial'].includes(pago)) {
    estadoCobro = 'pendiente';
    descripcionCobro = 'Cobro parcial';
  } else if (pago === 'impagado') {
    estadoCobro = 'pendiente';
    descripcionCobro = 'Marcado como impagado';
  } else {
    estadoCobro = 'pendiente';
    descripcionCobro = 'Pendiente de cobro';
  }
  facturar.push({
    clave: 'cobro',
    titulo: 'Cobro',
    estado: estadoCobro,
    descripcion: descripcionCobro,
    accion: estadoCobro === 'pendiente' ? { tipo: 'ir_facturacion', etiqueta: 'Ver cobros' } : null,
  });

  // Fase actual
  let faseActual = 'preparar';
  if (confirmado && !celebrado) faseActual = 'producir';
  else if (confirmado && celebrado) faseActual = 'facturar';
  if (cerrado) faseActual = null;

  const fases = [
    { clave: 'preparar', titulo: 'Preparar y confirmar', tareas: preparar },
    { clave: 'producir', titulo: 'Producir el evento', tareas: producir },
    { clave: 'facturar', titulo: 'Facturar y cobrar', tareas: facturar },
  ].map(f => ({
    ...f,
    actual: f.clave === faseActual,
    hechas: f.tareas.filter(t => t.estado === 'hecha').length,
    total: f.tareas.filter(t => t.estado !== 'info').length,
  }));

  const todas = fases.flatMap(f => f.tareas);
  const hechas = todas.filter(t => t.estado === 'hecha').length;
  const total = todas.filter(t => t.estado !== 'info').length;
  // El contador de la pestaña solo cuenta lo pendiente hasta la fase actual
  // (un borrador no acumula tareas de producción o de facturación)
  const indiceActual = fases.findIndex(f => f.actual);
  const pendientes = cerrado ? 0 : fases
    .filter((_, i) => i <= indiceActual)
    .flatMap(f => f.tareas)
    .filter(t => t.estado === 'pendiente').length;

  // Siguiente hito: la próxima fecha relevante (hoy incluido)
  let siguienteHito = null;
  if (!cerrado) {
    const candidatos = [];
    if (fechaLimiteCambios) candidatos.push({ fecha: fechaLimiteCambios, texto: 'fecha límite de cambios' });
    if (confirmado && porJornadas) {
      jornadas.forEach((j) => {
        const f = calcularFechaOrdenCocina(j.fecha);
        if (f) candidatos.push({ fecha: f, texto: `enviar la orden de cocina de la jornada ${j.numero}` });
      });
    } else if (confirmado && fechaOrdenCocina) {
      candidatos.push({ fecha: fechaOrdenCocina, texto: 'enviar la orden de cocina' });
    }
    if (fechaInicio) candidatos.push({ fecha: fechaInicio, texto: 'día del evento' });
    siguienteHito = candidatos
      .filter(c => c.fecha >= hoy)
      .sort((a, b) => a.fecha - b.fecha)[0] || null;
    if (!siguienteHito && confirmado && celebrado && !d.facturado) {
      siguienteHito = { fecha: null, texto: porJornadas && d.modoFacturacion === 'por_jornada'
        ? 'emitir las facturas de las jornadas' : 'emitir la factura final' };
    }
  }

  return { fases, hechas, total, pendientes, siguienteHito, fechaLimiteCambios, fechaOrdenCocina, diasParaEvento, faseActual, celebrado };
};
